import { useFormatter } from '../utils/formatters';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { accountCodesConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';
import {
  PageHeader, DataTableToolbar, PaginationBar, ActiveBadge,
  EmptyState, LoadingSpinner
} from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend, CartesianGrid
} from 'recharts';
import api from '../services/api';
import { toast } from 'sonner';
import { Plus, Download, BarChart2, TrendingUp, FileText } from 'lucide-react';
import { useDecimalFormat } from '../contexts/ExpenditureLabelsContext';
import AccountCodeModal from './AccountCodes/components/AccountCodeModal';
import ModuleReports from './Reports/ModuleReports';
import { ACCOUNT_CODE_REPORTS } from './Reports/reportConfigs';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const EMPTY = { code: '', description: '', budget: 0, active: true, is_favorite: false, monthly_budget: {} };

// ── Sidebar sub-nav for the 7 segments ───────────────────────────────────────
export function AccountCodesSidebar({ segments }) {
  return (
    <div className="space-y-0.5">
      {segments.filter(s => s.active).map(seg => (
        <NavLink
          key={seg.segment_number}
          to={`/account-codes/${seg.segment_number}`}
          className={({ isActive }) =>
            `sidebar-item text-xs ${ isActive ? 'active' : ''}`
          }
          aria-label={seg.label}
        >
          <span className="truncate">{seg.label}</span>
        </NavLink>
      ))}
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AccountCodesPage() {
  const { segmentNumber } = useParams();
  const navigate = useNavigate();
  const { fmtVal, fmtTotal } = useDecimalFormat();
  const { systemCurrency } = useFormatter();

  const [viewTab, setViewTab] = useState('analytics');
  const [segments, setSegments] = useState([]);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [sortState, handleSort] = useSortState('code', 'asc');

  // Load all segments once
  useEffect(() => {
    api.get('/api/account-codes/segments').then(res => {
      setSegments(res.data);
      // If no segment selected, redirect to first active one
      if (!segmentNumber && res.data.length > 0) {
        const first = res.data.find(s => s.active);
        if (first) navigate(`/account-codes/${first.segment_number}`, { replace: true });
      }
    }).catch(() => {});
  }, [segmentNumber, navigate]);

  // Load records when segment or search/page changes
  const load = useCallback(async () => {
    if (!segmentNumber) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/account-codes/${segmentNumber}`, {
        params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir }
      });
      setRecords(res.data.data);
      setTotal(res.data.total);
      setCurrentSegment(res.data.segment);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, [segmentNumber, search, page, pageSize, sortState]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSearch(''); }, [segmentNumber]);

  const openCreate = () => { setEditingRecord(null); setModalOpen(true); };
  const openEdit = r => { setEditingRecord(r); setModalOpen(true); };
  const toggleFavorite = async r => {
    try {
      const newVal = !r.is_favorite;
      await api.put(`/api/account-codes/${segmentNumber}/${r.code}`, { ...r, is_favorite: newVal });
      setRecords(prev => {
        const updated = prev.map(rec => rec.code === r.code ? { ...rec, is_favorite: newVal } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    } catch { toast.error('Failed to toggle favorite'); }
  };

  const segLabel = currentSegment?.label || `Account Code ${segmentNumber}`;
  const activeSegments = segments.filter(s => s.active);

  return (
    <AppShell accountSegments={activeSegments}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Account Codes</h1>
                <HelpButton guideId="account-codes" label="Account Codes Guide" />
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">Total records: {total}</p>
            </div>
          </div>
          <div className="flex gap-1 border-b mb-4" data-testid="ac-tabs">
            {[{ id: 'analytics', label: 'Analytics', icon: AnalyticsIcon }, { id: 'codes', label: segLabel }, { id: 'reports', label: 'Reports', icon: FileText }].map(t => (
              <button key={t.id} onClick={() => setViewTab(t.id)} data-testid={`ac-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  viewTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}>
                {t.icon && <t.icon className="h-3.5 w-3.5" />} {t.label}
              </button>
            ))}
          </div>

          {viewTab === 'analytics' && (segmentNumber ? (
            <DataFilesAnalytics {...accountCodesConfig} extraParams={{ segment_number: Number(segmentNumber) }} onCardClick={() => setViewTab('codes')} />
          ) : (
            <p className="text-sm text-muted-foreground py-8">Loading analytics…</p>
          ))}
          {viewTab === 'reports' && <ModuleReports reports={ACCOUNT_CODE_REPORTS} apiPrefix="/api/module-reports/account-codes" moduleLabel="Account Codes" />}

          {viewTab === 'codes' && (<>
          <div className="flex items-center justify-end mb-3">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid="ac-export"><Download className="h-4 w-4 mr-1" />Export</Button>
              <Button size="sm" onClick={openCreate} data-testid="ac-add-button"><Plus className="h-4 w-4 mr-1" />Add New</Button>
            </div>
          </div>

          <DataTableToolbar
            searchValue={search}
            onSearch={v => { setSearch(v); setPage(1); }}
            onClear={() => { setSearch(''); setPage(1); }}
            searchPlaceholder={`Search ${segLabel}...`}
          />

          <div className="rounded-xl border bg-card">
            {loading ? (
              <div className="py-12"><LoadingSpinner /></div>
            ) : records.length === 0 ? (
              <EmptyState
                title={`No ${segLabel} codes found`}
                description="Add codes to this account code segment."
                action={<Button size="sm" onClick={openCreate}>Add {segLabel}</Button>}
              />
            ) : (
              <Table className="data-table" aria-label={`${segLabel} codes`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <SortableTableHeader label={segLabel} field="code" sortState={sortState} onSort={handleSort} />
                    <SortableTableHeader label="Description" field="description" sortState={sortState} onSort={handleSort} />
                    <SortableTableHeader label={`Budget (${systemCurrency})`} field="budget" sortState={sortState} onSort={handleSort} className="text-right" />
                    <SortableTableHeader label="Active" field="active" sortState={sortState} onSort={handleSort} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map(r => (
                    <TableRow
                      key={r.code}
                      className="cursor-pointer"
                      onClick={() => openEdit(r)}
                    >
                      <TableCell onClick={e => e.stopPropagation()}>
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-primary">{r.code}</TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell className="text-right [font-variant-numeric:tabular-nums]">
                        {fmtTotal(parseFloat(r.budget) || 0)}
                      </TableCell>
                      <TableCell><ActiveBadge active={r.active} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="px-4">
              <PaginationBar
                page={page} pageSize={pageSize} total={total}
                onPageChange={setPage}
                onPageSizeChange={v => { setPageSize(v); setPage(1); }}
              />
            </div>
          </div>

      {/* Edit/Create Modal */}
      <AccountCodeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        segmentNumber={parseInt(segmentNumber)}
        segmentLabel={segLabel}
        initialData={editingRecord}
        onSaved={load}
      />
      </>)}
    </AppShell>
  );
}
